import { createContext, useState, useEffect, useCallback, useRef } from "react";
import { getNotificationCount } from "../../services/apiCalls";

const POLLING_INTERVAL_MS = 60000;

export const NotificationContext = createContext({
  unreadCount: 0,
  totalCount: 0,
  refreshCount: () => {},
});

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const intervalRef = useRef(null);

  const refreshCount = useCallback(async () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return;

    try {
      const data = await getNotificationCount();
      setUnreadCount(data.unread_count || 0);
      setTotalCount(data.total_count || 0);
    } catch (error) {
      console.error("Error refreshing notification count:", error);
    }
  }, []);

  // Start/stop polling based on tab visibility
  useEffect(() => {
    const startPolling = () => {
      if (intervalRef.current) return; // already running
      refreshCount();
      intervalRef.current = setInterval(refreshCount, POLLING_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        startPolling();
      }
    };

    // Only start if tab is currently visible
    if (!document.hidden) {
      startPolling();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshCount]);

  return (
    <NotificationContext.Provider
      value={{ unreadCount, totalCount, refreshCount }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
